'use client'

import { useEffect, useState } from 'react'
import { useTranslate } from '@tolgee/react'
import { AlertCircleIcon, toast } from 'oddle-shop-monorepo.design-system'
import { H4, P } from '@/components/common/Typo'
import {
  formatPaymentLockCountdown,
  getPaymentLockSecondsLeft,
  PaymentLock,
} from '@/utils/paymentLock'

const PAYMENT_IN_PROGRESS_TOAST_ID = 'payment-in-progress'

const PaymentInProgressToastContent = ({
  expiresAt,
}: {
  expiresAt: number | null
}) => {
  const { t } = useTranslate()
  const [secondsLeft, setSecondsLeft] = useState(() =>
    expiresAt ? getPaymentLockSecondsLeft(expiresAt) : null,
  )

  useEffect(() => {
    if (!expiresAt) return
    const timer = setInterval(() => {
      const next = getPaymentLockSecondsLeft(expiresAt)
      setSecondsLeft(next)
      if (next <= 0) {
        clearInterval(timer)
        toast.dismiss(PAYMENT_IN_PROGRESS_TOAST_ID)
      }
    }, 1000)
    return () => clearInterval(timer)
  }, [expiresAt])

  return (
    <div className="flex items-start gap-2.5 p-2.5">
      <AlertCircleIcon
        className="text-headings h-6 w-6 flex-shrink-0"
        aria-hidden="true"
      />
      <div>
        <H4 bold margin="none">
          {t(
            'A payment for this order is already in progress. Complete it in the other tab, or try again when the timer ends.',
          )}
        </H4>
        {secondsLeft !== null && secondsLeft > 0 && (
          <P margin="none" className="mt-1">
            {t(
              'Try again in {minutes}:{seconds}',
              'Try again in {minutes}:{seconds}',
              formatPaymentLockCountdown(secondsLeft),
            )}
          </P>
        )}
      </div>
    </div>
  )
}

// One toast for the whole app: every cart write refused while the order is
// payment-locked lands here (see services/local.ts), so a burst of refusals
// updates the same toast instead of stacking.
export const showPaymentInProgressToast = ({ expiresAt }: PaymentLock) => {
  // Plain toast styled like AddToCartToast so it follows the menu theme;
  // toast.warning would pick up the Toaster's fixed yellow.
  toast(<PaymentInProgressToastContent expiresAt={expiresAt} />, {
    id: PAYMENT_IN_PROGRESS_TOAST_ID,
    position: 'top-right',
    classNames: {
      toast: '!bg-container',
      content: 'w-full',
    },
    // Short, like the shop's other toasts: it sits over the header, and each
    // refused retry re-shows it with the current time left.
    duration: 5000,
  })
}
